package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.create_event_button
import rainbowclubs.composeapp.generated.resources.create_event_icon_cd
import rainbowclubs.composeapp.generated.resources.create_event_page_description
import rainbowclubs.composeapp.generated.resources.create_new_event
import rainbowclubs.composeapp.generated.resources.event_details
import rainbowclubs.composeapp.generated.resources.event_details_icon_cd
import rainbowclubs.composeapp.generated.resources.ic_calendar

@Composable
fun CreateEventScreen(
    clubId: String,
    viewModel: CreateEventViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (CreateEventViewModel.NavigationEvent) -> Unit,
) {
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            CreateEventScreenContent(
                uiState = uiState,
                navigateTo = navigateTo,
                onTitleChanged = viewModel::onTitleChanged,
                onDescriptionChanged = viewModel::onDescriptionChanged,
                onLocationChanged = viewModel::onLocationChanged,
                onDateChanged = viewModel::onDateChanged,
                onEndDateChanged = viewModel::onEndDateChanged,
                onStartsAtChanged = viewModel::onStartsAtChanged,
                onEndsAtChanged = viewModel::onEndsAtChanged,
                onSubmitClick = viewModel::createEvent,
            )
        }
    }
}

@Composable
private fun CreateEventScreenContent(
    uiState: EventFormUiState,
    navigateTo: (CreateEventViewModel.NavigationEvent) -> Unit,
    onTitleChanged: (String) -> Unit,
    onDescriptionChanged: (String) -> Unit,
    onLocationChanged: (String) -> Unit,
    onDateChanged: (String) -> Unit,
    onEndDateChanged: (String) -> Unit,
    onStartsAtChanged: (String) -> Unit,
    onEndsAtChanged: (String) -> Unit,
    onSubmitClick: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        EventFormHeader(
            title = Res.string.create_new_event,
            description = Res.string.create_event_page_description,
            icon = Res.drawable.ic_calendar,
            iconContentDescription = Res.string.create_event_icon_cd,
            onBackClick = { navigateTo(CreateEventViewModel.NavigationEvent.Back) },
        )

        Spacer(modifier = Modifier.height(52.dp))

        EventFormSection(
            title = Res.string.event_details,
            iconContentDescription = Res.string.event_details_icon_cd,
        )

        Spacer(modifier = Modifier.height(28.dp))

        EventFormFields(
            form = uiState,
            onTitleChanged = onTitleChanged,
            onDescriptionChanged = onDescriptionChanged,
            onLocationChanged = onLocationChanged,
            onDateChanged = onDateChanged,
            onEndDateChanged = onEndDateChanged,
            onStartsAtChanged = onStartsAtChanged,
            onEndsAtChanged = onEndsAtChanged,
        )

        Spacer(modifier = Modifier.height(20.dp))

        EventValidationError(error = uiState.validationError)

        Spacer(modifier = Modifier.height(20.dp))

        FormErrorText(error = uiState.formError)

        Spacer(modifier = Modifier.height(28.dp))

        EventSubmitButton(
            text = Res.string.create_event_button,
            isSubmitting = uiState.isSubmitting,
            onClick = onSubmitClick,
        )

        Spacer(modifier = Modifier.height(36.dp))
    }
}

@Preview(name = "Create Event Screen")
@Composable
private fun CreateEventScreenPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                CreateEventScreenContent(
                    uiState = EventFormUiState(
                        title = "Board Game Night",
                        description = "Games, snacks, and community time",
                        location = "Rainbow Club Room",
                        date = "2026-06-23",
                        endDate = "2026-06-23",
                        startsAt = "17:00",
                        endsAt = "20:30",
                    ),
                    navigateTo = {},
                    onTitleChanged = {},
                    onDescriptionChanged = {},
                    onLocationChanged = {},
                    onDateChanged = {},
                    onEndDateChanged = {},
                    onStartsAtChanged = {},
                    onEndsAtChanged = {},
                    onSubmitClick = {},
                )
            }
        }
    }
}
